import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Badge } from "@/components/kit";
import {
  IdentificationCard,
  IdentificationBadge,
  UserCircle,
  UploadSimple,
  DownloadSimple,
  Trash,
  Eye,
  FilePdf,
  Camera,
} from "@phosphor-icons/react";

const SLOTS = [
  { kind: "photo", label: "Customer Photo", icon: UserCircle, accept: "image/*", hint: "JPG or PNG, max 5 MB" },
  { kind: "pan", label: "PAN Card", icon: IdentificationBadge, accept: "image/*,application/pdf", hint: "Image or PDF, max 5 MB" },
  { kind: "aadhaar", label: "Aadhaar Card", icon: IdentificationCard, accept: "image/*,application/pdf", hint: "Image or PDF, max 5 MB" },
];

export default function CustomerDocuments({ customerId, onChange }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [busyKind, setBusyKind] = useState("");
  const [err, setErr] = useState("");
  const inputs = useRef({});
  const cameraInputs = useRef({});
  // Blob URL cache: docId -> objectURL. Rebuilt when docs list changes.
  const [previews, setPreviews] = useState({}); // {docId: {url, contentType}}

  const canWrite = ["super_admin", "branch_admin", "staff"].includes(user.role);
  const token = localStorage.getItem("access_token");

  const load = async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/documents`);
      setDocs(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load documents");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  // Fetch image previews (blob) so we don't leak the bearer token in <img src>
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const d of docs) {
        if (!(d.content_type || "").startsWith("image/")) continue;
        try {
          const res = await api.get(
            `/customers/${customerId}/documents/${d.id}/download?disposition=inline`,
            { responseType: "blob" }
          );
          const url = URL.createObjectURL(res.data);
          next[d.id] = { url, contentType: d.content_type };
        } catch { /* skip */ }
      }
      if (!cancelled) {
        // Revoke previous URLs
        Object.values(previews).forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
        setPreviews(next);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs.map((d) => d.id).join(",")]);

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErr("File too large. Max 5 MB.");
      return;
    }
    setErr(""); setBusyKind(kind);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try {
      await api.post(`/customers/${customerId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
      onChange && onChange();
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusyKind("");
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(
        `/customers/${customerId}/documents/${doc.id}/download?disposition=attachment`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${doc.kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setErr("Download failed");
    }
  };

  const view = (doc) => {
    const p = previews[doc.id];
    if (p) {
      window.open(p.url, "_blank");
      return;
    }
    // PDF or other: fetch blob and open
    api.get(`/customers/${customerId}/documents/${doc.id}/download?disposition=inline`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank");
      })
      .catch(() => setErr("Could not open document"));
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.kind}?`)) return;
    try {
      await api.delete(`/customers/${customerId}/documents/${doc.id}`);
      await load();
      onChange && onChange();
    } catch (e) {
      setErr(e.response?.data?.detail || "Delete failed");
    }
  };

  const byKind = Object.fromEntries(docs.map((d) => [d.kind, d]));

  return (
    <Card className="mb-6">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="heading font-semibold">Documents</h3>
        <span className="text-xs text-slate-500">Photo · PAN · Aadhaar</span>
      </div>

      {err && (
        <div className="mx-5 mt-4 text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          {String(err)}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {SLOTS.map((slot) => {
          const doc = byKind[slot.kind];
          const preview = doc ? previews[doc.id] : null;
          const isPdf = doc?.content_type === "application/pdf";
          const Icon = slot.icon;
          return (
            <div
              key={slot.kind}
              className="border border-slate-200 rounded-xl overflow-hidden flex flex-col"
              data-testid={`doc-slot-${slot.kind}`}
            >
              <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden relative">
                {doc && preview ? (
                  <img
                    src={preview.url}
                    alt={slot.label}
                    className="w-full h-full object-cover"
                    data-testid={`doc-preview-${slot.kind}`}
                  />
                ) : doc && isPdf ? (
                  <div className="flex flex-col items-center text-rose-600">
                    <FilePdf size={44} weight="fill" />
                    <div className="text-xs mt-1 text-slate-600 mono truncate max-w-[80%]">{doc.filename}</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-slate-300">
                    <Icon size={44} weight="regular" />
                    <div className="text-xs mt-1 text-slate-400">No file uploaded</div>
                  </div>
                )}
              </div>

              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{slot.label}</div>
                    {doc ? (
                      <div className="text-[11px] text-slate-500 mono truncate max-w-[180px]">
                        {(doc.size / 1024).toFixed(0)} KB · {doc.content_type.split("/")[1]?.toUpperCase()}
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-400">{slot.hint}</div>
                    )}
                  </div>
                  {doc && <Badge tone="success">Uploaded</Badge>}
                </div>

                <div className="flex gap-1.5 flex-wrap mt-auto pt-1">
                  {doc ? (
                    <>
                      <button
                        onClick={() => view(doc)}
                        className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                        data-testid={`doc-view-${slot.kind}`}
                        title="View"
                      >
                        <Eye size={13} /> View
                      </button>
                      <button
                        onClick={() => download(doc)}
                        className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        data-testid={`doc-download-${slot.kind}`}
                        title="Download"
                      >
                        <DownloadSimple size={13} weight="bold" /> Download
                      </button>
                      {canWrite && (
                        <>
                          {slot.kind === "photo" && (
                            <button
                              onClick={() => cameraInputs.current[slot.kind]?.click()}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100"
                              data-testid={`doc-recapture-${slot.kind}`}
                              title="Retake photo"
                            >
                              <Camera size={13} weight="bold" /> Retake
                            </button>
                          )}
                          <button
                            onClick={() => inputs.current[slot.kind]?.click()}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                            data-testid={`doc-replace-${slot.kind}`}
                            title="Replace"
                          >
                            <UploadSimple size={13} /> Replace
                          </button>
                          <button
                            onClick={() => remove(doc)}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                            data-testid={`doc-delete-${slot.kind}`}
                            title="Delete"
                          >
                            <Trash size={13} />
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    canWrite ? (
                      <div className="w-full flex gap-1.5">
                        {slot.kind === "photo" && (
                          <button
                            onClick={() => cameraInputs.current[slot.kind]?.click()}
                            disabled={busyKind === slot.kind}
                            className="flex-1 flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md bg-sky-600 hover:bg-sky-700 text-white font-medium disabled:opacity-60"
                            data-testid={`doc-capture-${slot.kind}`}
                          >
                            <Camera size={14} weight="bold" /> Take photo
                          </button>
                        )}
                        <button
                          onClick={() => inputs.current[slot.kind]?.click()}
                          disabled={busyKind === slot.kind}
                          className="flex-1 flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-medium disabled:opacity-60"
                          data-testid={`doc-upload-${slot.kind}`}
                        >
                          <UploadSimple size={14} weight="bold" />
                          {busyKind === slot.kind ? "Uploading…" : "Upload"}
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 py-2">No upload permission.</div>
                    )
                  )}
                </div>

                <input
                  ref={(el) => (inputs.current[slot.kind] = el)}
                  type="file"
                  accept={slot.accept}
                  className="hidden"
                  data-testid={`doc-input-${slot.kind}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) upload(slot.kind, f);
                  }}
                />
                {slot.kind === "photo" && (
                  <input
                    ref={(el) => (cameraInputs.current[slot.kind] = el)}
                    type="file"
                    accept="image/*"
                    capture="user"
                    className="hidden"
                    data-testid={`doc-camera-${slot.kind}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) upload(slot.kind, f);
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
